import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';

import {
  ConciliacionAbonosService,
  DetalleEntrada,
  EstadoEntrada,
  FilaEntrada,
  MonedaConciliacion,
  PaginaEntradas,
  PaisConciliacion,
  ResumenEntradas,
} from '../../services/conciliacion-abonos.service';

type Periodo = 'hoy' | '7' | '30' | 'rango';

/** Una fila de la tabla: o el encabezado de un día, o una entrada. */
type ItemLista = { tipo: 'dia'; etiqueta: string } | { tipo: 'fila'; fila: FilaEntrada; clave: string };

interface InfoEstado {
  etiqueta: string;
  /** Clase de la píldora. */
  clase: string;
  /** Color del tramo de la barra de reparto. */
  color: string;
}

/**
 * Etiquetas y colores de cada estado. Los colores se distinguen también por
 * luminosidad (no solo por tono), para que se lean con daltonismo.
 */
export const ESTADOS: Record<EstadoEntrada, InfoEstado> = {
  OK: { etiqueta: 'Acreditado', clase: 'b-ok', color: '#1C593D' },
  REP: { etiqueta: 'Reparado a mano · verificar', clase: 'b-rep', color: '#0e7490' },
  SINABONO: { etiqueta: 'Asignado sin abono', clase: 'b-sinabono', color: '#3b82f6' },
  RET: { etiqueta: 'Retenido', clase: 'b-retenido', color: '#f59e0b' },
  SIN: { etiqueta: 'Sin asignar', clase: 'b-sinasignar', color: '#c2410c' },
  BANCO: { etiqueta: 'Sin cartola', clase: 'b-banco', color: '#7c3aed' },
  DIS: { etiqueta: 'Cuenta propia', clase: 'b-diseno', color: '#9ca3af' },
  // Depósitos a Binance que nadie reclamó: en su mayoría tesorería. Se ven,
  // pero no son pendientes (decisión del dueño, 22/10).
  NOREC: { etiqueta: 'Sin reclamar', clase: 'b-norec', color: '#64748b' },
};

/** Lo que alguien tiene que mirar. Igual que `POR_RESOLVER` del backend. */
const POR_RESOLVER: EstadoEntrada[] = ['REP', 'SINABONO', 'RET', 'SIN', 'BANCO'];

const ESTADOS_DE: Record<PaisConciliacion, EstadoEntrada[]> = {
  CL: ['OK', 'REP', 'SINABONO', 'RET', 'SIN', 'BANCO', 'DIS'],
  VE: ['OK', 'SINABONO', 'SIN', 'DIS'],
  USDT: ['OK', 'SINABONO', 'NOREC'],
};

const PAISES: Array<{ clave: PaisConciliacion; codigo: string; nombre: string }> = [
  { clave: 'CL', codigo: 'CL', nombre: 'Chile · CLP' },
  { clave: 'VE', codigo: 'VE', nombre: 'Venezuela · VES' },
  { clave: 'USDT', codigo: '₮', nombre: 'USDT' },
];

const ZONA = 'America/Santiago';

@Component({
  selector: 'app-conciliacion-abonos',
  templateUrl: './conciliacion-abonos.component.html',
  styleUrls: ['./conciliacion-abonos.component.sass'],
})
export class ConciliacionAbonosComponent implements OnInit, OnDestroy {
  @ViewChild('panelDetalle') panelDetalle?: ElementRef<HTMLElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly busqueda$ = new Subject<void>();
  private peticionLista?: Subscription;
  private peticionResumen?: Subscription;
  private peticionDetalle?: Subscription;

  /** Resúmenes ya traídos, por `pais|desde|hasta`: cambiar de pestaña no vuelve a pedirlos. */
  private resumenes = new Map<string, ResumenEntradas>();

  readonly paises = PAISES;
  readonly estados = ESTADOS;
  readonly opcionesLimite = [25, 50, 100];

  // Filtros (viven en la URL).
  pais: PaisConciliacion = 'CL';
  periodo: Periodo = 'hoy';
  desde = '';
  hasta = '';
  estado: EstadoEntrada | '' = '';
  cuenta = '';
  buscar = '';
  pagina = 1;
  limite = 25;

  resumen: ResumenEntradas | null = null;
  pendientesPorPais: Partial<Record<PaisConciliacion, number>> = {};
  lista: PaginaEntradas | null = null;
  items: ItemLista[] = [];
  seleccion: string | null = null;
  detalle: DetalleEntrada | null = null;

  cargandoResumen = false;
  cargandoLista = false;
  cargandoDetalle = false;
  descargando = false;
  error = '';
  actualizado: Date | null = null;

  constructor(
    private readonly servicio: ConciliacionAbonosService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snack: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.busqueda$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.aUrl({ buscar: this.buscar.trim() || undefined, pagina: undefined }));

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.desdeUrl(params));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.peticionLista?.unsubscribe();
    this.peticionResumen?.unsubscribe();
    this.peticionDetalle?.unsubscribe();
  }

  // ─── Estado <-> URL ─────────────────────────────────────────────────────────

  private desdeUrl(params: Params): void {
    const pais = String(params['pais'] ?? 'CL').toUpperCase();
    const paisAnterior = this.pais;
    const periodoAnterior = `${this.desde}|${this.hasta}`;
    this.pais = (['CL', 'VE', 'USDT'].includes(pais) ? pais : 'CL') as PaisConciliacion;

    const hoy = this.hoy();
    const periodo = String(params['periodo'] ?? 'hoy') as Periodo;
    this.periodo = (['hoy', '7', '30', 'rango'] as Periodo[]).includes(periodo) ? periodo : 'hoy';
    if (this.periodo === 'rango') {
      this.desde = this.fechaValida(params['desde']) ?? hoy;
      this.hasta = this.fechaValida(params['hasta']) ?? hoy;
      if (this.desde > this.hasta) [this.desde, this.hasta] = [this.hasta, this.desde];
    } else {
      this.hasta = hoy;
      this.desde = this.periodo === 'hoy' ? hoy : this.sumarDias(hoy, -(Number(this.periodo) - 1));
    }

    const estado = String(params['estado'] ?? '');
    this.estado = ESTADOS_DE[this.pais].includes(estado as EstadoEntrada) ? (estado as EstadoEntrada) : '';
    this.cuenta = String(params['cuenta'] ?? '');
    // Mientras el operador escribe, la URL va atrasada respecto del input:
    // pisarlo le borraría lo último que tecleó.
    const escribiendo = typeof document !== 'undefined' && document.activeElement?.id === 'ca-buscar';
    if (!escribiendo) this.buscar = String(params['buscar'] ?? '');
    this.pagina = Math.max(1, Number(params['pagina']) || 1);
    const limite = Number(params['limite']);
    this.limite = this.opcionesLimite.includes(limite) ? limite : 25;

    const cambioPeriodo = periodoAnterior !== `${this.desde}|${this.hasta}`;
    // Con el resumen en camino no se relanza: cada tecla del buscador
    // cancelaría y volvería a pedir las tres consultas pesadas.
    if (cambioPeriodo || (!this.resumen && !this.cargandoResumen)) {
      // Período nuevo: los resúmenes guardados ya no sirven.
      this.resumenes.clear();
      this.cargarResumenes();
    } else if (paisAnterior !== this.pais) {
      this.mostrarResumenGuardado();
    }
    this.cargarLista();
  }

  private aUrl(cambios: Record<string, unknown>): void {
    const actual = {
      pais: this.pais,
      periodo: this.periodo,
      desde: this.periodo === 'rango' ? this.desde : undefined,
      hasta: this.periodo === 'rango' ? this.hasta : undefined,
      estado: this.estado || undefined,
      cuenta: this.cuenta || undefined,
      buscar: this.buscar.trim() || undefined,
      pagina: this.pagina > 1 ? this.pagina : undefined,
      limite: this.limite !== 25 ? this.limite : undefined,
    };
    const queryParams: Params = {};
    for (const [k, v] of Object.entries({ ...actual, ...cambios })) {
      if (v !== undefined && v !== null && v !== '') queryParams[k] = v;
    }
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  // ─── Acciones de la pantalla ────────────────────────────────────────────────

  elegirPais(pais: PaisConciliacion): void {
    if (pais === this.pais) return;
    this.cerrarDetalle();
    this.aUrl({ pais, estado: undefined, cuenta: undefined, pagina: undefined });
  }

  elegirPeriodo(periodo: Periodo): void {
    this.cerrarDetalle();
    if (periodo === 'rango') {
      this.aUrl({ periodo, desde: this.desde, hasta: this.hasta, pagina: undefined });
      return;
    }
    this.aUrl({ periodo, desde: undefined, hasta: undefined, pagina: undefined });
  }

  /** Una fecha borrada o inválida no se manda: se vuelve a la que había. */
  cambiarRango(): void {
    const desde = this.fechaValida(this.desde);
    const hasta = this.fechaValida(this.hasta);
    if (!desde || !hasta) {
      this.desde = this.lista?.desde ?? this.resumen?.desde ?? this.hoy();
      this.hasta = this.lista?.hasta ?? this.resumen?.hasta ?? this.hoy();
      return;
    }
    this.cerrarDetalle();
    this.aUrl({ periodo: 'rango', desde, hasta, pagina: undefined });
  }

  elegirEstado(estado: EstadoEntrada | ''): void {
    this.aUrl({ estado: estado || undefined, pagina: undefined });
  }

  elegirCuenta(cuenta: string): void {
    this.cuenta = this.cuenta === cuenta ? '' : cuenta;
    this.cambiarCuenta();
  }

  cambiarCuenta(): void {
    this.aUrl({ cuenta: this.cuenta || undefined, pagina: undefined });
  }

  alEscribir(): void {
    this.busqueda$.next();
  }

  irAPagina(pagina: number): void {
    const destino = Math.min(Math.max(1, pagina), Math.max(1, this.totalPaginas));
    this.aUrl({ pagina: destino > 1 ? destino : undefined });
  }

  cambiarLimite(): void {
    this.aUrl({ limite: this.limite !== 25 ? this.limite : undefined, pagina: undefined });
  }

  refrescar(): void {
    // "Hoy" se recalcula: pasada la medianoche ya es otro día.
    if (this.periodo !== 'rango' && this.hasta !== this.hoy()) {
      this.desdeUrl({ ...this.route.snapshot.queryParams });
      return;
    }
    this.resumenes.clear();
    this.cargarResumenes();
    this.cargarLista();
    if (this.detalle) this.abrir(this.detalle.fila, false);
  }

  // ─── Carga ──────────────────────────────────────────────────────────────────

  private claveResumen(pais: PaisConciliacion): string {
    return `${pais}|${this.desde}|${this.hasta}`;
  }

  private mostrarResumenGuardado(): void {
    this.resumen = this.resumenes.get(this.claveResumen(this.pais)) ?? null;
    if (!this.resumen) this.cargarResumenes();
  }

  /**
   * El resumen de las tres pestañas de una vez (las otras solo para su
   * contador de pendientes), y solo cuando cambia el período o se actualiza.
   */
  private cargarResumenes(): void {
    this.peticionResumen?.unsubscribe();
    this.resumen = null;
    this.cargandoResumen = true;
    const pedir = (p: PaisConciliacion) =>
      this.servicio.resumen(p, this.desde, this.hasta).pipe(catchError(() => of(null)));
    this.peticionResumen = forkJoin(PAISES.map((p) => pedir(p.clave))).subscribe({
      next: (resumenes) => {
        resumenes.forEach((r, i) => {
          const pais = PAISES[i].clave;
          this.pendientesPorPais[pais] = r ? r.porResolver.cantidad : undefined;
          if (r) this.resumenes.set(this.claveResumen(pais), r);
        });
        this.resumen = this.resumenes.get(this.claveResumen(this.pais)) ?? null;
        if (!this.resumen) this.error = 'No se pudo cargar el resumen.';
        this.cargandoResumen = false;
        this.cdr.detectChanges();
      },
    });
  }

  private cargarLista(): void {
    this.peticionLista?.unsubscribe();
    this.cargandoLista = true;
    this.error = '';
    this.peticionLista = this.servicio
      .movimientos({
        pais: this.pais,
        desde: this.desde,
        hasta: this.hasta,
        estados: this.estado ? [this.estado] : undefined,
        cuentas: this.cuenta ? [this.cuenta] : undefined,
        buscar: this.buscar.trim() || undefined,
        pagina: this.pagina,
        limite: this.limite,
      })
      .subscribe({
        next: (p) => {
          this.lista = p;
          this.cargandoLista = false;
          // Un link viejo puede pedir una página que ya no existe: se va a la última.
          const ultima = Math.max(1, Math.ceil(p.total / p.limite));
          if (!p.filas.length && p.total > 0 && p.pagina > ultima) {
            this.irAPagina(ultima);
            return;
          }
          this.items = this.agruparPorDia(p.filas);
          this.actualizado = new Date();
          // En escritorio se abre la primera fila, como en la propuesta.
          const sigue = p.filas.find((f) => this.clave(f) === this.seleccion);
          if (!sigue) {
            this.cerrarDetalle();
            if (p.filas.length && window.innerWidth > 760) this.abrir(p.filas[0], false);
          }
          this.cdr.detectChanges();
        },
        error: (e) => {
          this.cargandoLista = false;
          this.error = this.mensajeDeError(e);
          this.cdr.detectChanges();
        },
      });
  }

  abrir(fila: FilaEntrada, desplazar = true): void {
    const anterior = this.seleccion;
    this.seleccion = this.clave(fila);
    this.peticionDetalle?.unsubscribe();
    this.cargandoDetalle = true;
    this.peticionDetalle = this.servicio.detalle(fila.origen, fila.id).subscribe({
      next: (d) => {
        this.detalle = d;
        this.cargandoDetalle = false;
        this.cdr.detectChanges();
        // En el celular el detalle queda debajo de la lista: se lleva a la vista.
        if (desplazar && window.innerWidth <= 1100) {
          this.panelDetalle?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      error: (e) => {
        this.cargandoDetalle = false;
        // La fila resaltada tiene que ser la del detalle que se ve.
        this.seleccion = this.detalle ? anterior : null;
        this.snack.open(this.mensajeDeError(e), 'Cerrar', { duration: 5000 });
        this.cdr.detectChanges();
      },
    });
  }

  cerrarDetalle(): void {
    this.peticionDetalle?.unsubscribe();
    this.cargandoDetalle = false;
    this.seleccion = null;
    this.detalle = null;
  }

  async exportar(): Promise<void> {
    if (this.descargando) return;
    this.descargando = true;
    try {
      // El Excel baja exactamente el período que se está viendo (la pantalla
      // acorta a 62 días y el export aceptaría más).
      await this.servicio.descargar({
        pais: this.pais,
        desde: this.lista?.desde ?? this.desde,
        hasta: this.lista?.hasta ?? this.hasta,
        estados: this.estado ? [this.estado] : undefined,
        cuentas: this.cuenta ? [this.cuenta] : undefined,
        buscar: this.buscar.trim() || undefined,
      });
    } catch (e) {
      this.snack.open(this.mensajeDeError(e), 'Cerrar', { duration: 6000 });
    } finally {
      this.descargando = false;
      this.cdr.detectChanges();
    }
  }

  // ─── Navegación a otras pantallas ───────────────────────────────────────────

  verMovimientos(fila: FilaEntrada, evento?: Event): void {
    evento?.stopPropagation();
    const c = fila.cliente;
    if (!c?.email) return;
    this.router.navigate(['/wallet/admin/user-balance', c.usuarioId, c.email]);
  }

  /** Qué botón ofrece una entrada sin resolver y adónde lleva. */
  accion(fila: FilaEntrada): { etiqueta: string; ruta: string } | null {
    // Lo acreditado y lo que es de cuentas propias no tiene nada que asignar.
    if (fila.estado === 'OK' || fila.estado === 'DIS' || fila.estado === 'REP') return null;
    if (fila.cliente && fila.estado !== 'SINABONO') return null;
    switch (fila.origen) {
      case 'CARTOLA':
        return fila.estado === 'SINABONO'
          ? null
          : { etiqueta: fila.estado === 'RET' ? 'Revisar y asignar' : 'Asignar', ruta: '/wallet/admin/unassigned-transaction' };
      case 'SHINKANSEN':
        return { etiqueta: 'Cargar Excel BCI', ruta: '/wallet/admin/manual-xls-bci' };
      case 'N58':
      case 'BP':
      case 'R4':
        return fila.estado === 'SIN' ? { etiqueta: 'Registrar referencia', ruta: '/wallet/admin/referencias-ves-admin' } : null;
      case 'BINANCE_LINK':
        return { etiqueta: 'Ver centralizaciones', ruta: '/wallet/admin/binance-centralizations' };
      default:
        return null;
    }
  }

  irA(ruta: string, evento?: Event): void {
    evento?.stopPropagation();
    this.router.navigate([ruta]);
  }

  /** Teclado en una fila: Enter o espacio abren el detalle (no los botones de adentro). */
  teclaFila(evento: KeyboardEvent, fila: FilaEntrada): void {
    if (evento.target !== evento.currentTarget) return;
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      this.abrir(fila);
    }
  }

  // ─── Lo que pinta la pantalla ───────────────────────────────────────────────

  get moneda(): MonedaConciliacion {
    return this.pais === 'CL' ? 'CLP' : this.pais === 'VE' ? 'VES' : 'USDT';
  }

  /** Tramos de la barra de reparto, en el orden de los estados (sin los ajustes no se puede: van en la leyenda). */
  get segmentos(): Array<{ estado: EstadoEntrada; monto: number; peso: number }> {
    const r = this.resumen;
    if (!r || !r.total.monto) return [];
    return r.porEstado.filter((e) => e.monto > 0).map((e) => ({ estado: e.estado, monto: e.monto, peso: e.monto }));
  }

  get leyenda() {
    return this.resumen?.porEstado ?? [];
  }

  /**
   * Lo que entró de clientes: sin ajustes, sin cuentas propias y sin USDT que
   * nadie reclamó. Es el número grande y la base del % acreditado.
   */
  get montoClientes(): number {
    const e = this.resumen?.entradas;
    return e ? e.monto - e.montoPropio - e.montoSinReclamar : 0;
  }

  /** % acreditado sobre lo que entró de clientes. */
  get pctAsignado(): string {
    const e = this.resumen?.entradas;
    if (!e || this.montoClientes <= 0) return '—';
    return `${Math.round((e.montoOk / this.montoClientes) * 100)} %`;
  }

  /** Cuentas reales (sin las pseudo-cuentas de ajuste) para el "N cuentas". */
  get cantidadCuentas(): number {
    return this.cuentasDelFiltro.filter((c) => !c.ajuste).length;
  }

  get pendientes() {
    return (this.resumen?.porEstado ?? []).filter((e) => POR_RESOLVER.includes(e.estado) && e.cantidad > 0);
  }

  get chips(): Array<{ estado: EstadoEntrada | ''; etiqueta: string; cantidad: number }> {
    const r = this.resumen;
    const todos = { estado: '' as const, etiqueta: 'Todos', cantidad: r?.total.cantidad ?? 0 };
    return [
      todos,
      ...ESTADOS_DE[this.pais].map((estado) => ({
        estado,
        etiqueta: ESTADOS[estado].etiqueta,
        cantidad: r?.porEstado.find((e) => e.estado === estado)?.cantidad ?? 0,
      })),
    ];
  }

  get cuentasDelFiltro() {
    return this.resumen?.porCuenta ?? [];
  }

  /** Partes de la barra fina de una cuenta; los tramos en cero no se dibujan. */
  tramosCuenta(c: { monto: number; montoOk: number; montoPendiente: number }) {
    return [
      { valor: Math.max(0, c.montoOk), color: '#1C593D' },
      { valor: Math.max(0, c.montoPendiente), color: '#f59e0b' },
      { valor: Math.max(0, c.monto - c.montoOk - c.montoPendiente), color: '#d1d5db' },
    ].filter((t) => t.valor > 0.005);
  }

  get totalPaginas(): number {
    const l = this.lista;
    return l ? Math.max(1, Math.ceil(l.total / l.limite)) : 1;
  }

  get desdeFila(): number {
    const l = this.lista;
    return l && l.total && l.filas.length ? (l.pagina - 1) * l.limite + 1 : 0;
  }

  get hastaFila(): number {
    const l = this.lista;
    return l && l.filas.length ? (l.pagina - 1) * l.limite + l.filas.length : 0;
  }

  /** Botones de página: la actual con dos vecinas a cada lado, más la primera y la última. */
  get paginas(): Array<number | '…'> {
    const total = this.totalPaginas;
    const actual = this.lista?.pagina ?? 1;
    const ventana = new Set<number>([1, total]);
    for (let p = actual - 2; p <= actual + 2; p++) if (p >= 1 && p <= total) ventana.add(p);
    const ordenadas = [...ventana].sort((a, b) => a - b);
    const salida: Array<number | '…'> = [];
    ordenadas.forEach((p, i) => {
      if (i && p - ordenadas[i - 1] > 1) salida.push('…');
      salida.push(p);
    });
    return salida;
  }

  /** Lo que se ve, según el período que de verdad respondió el backend. */
  get etiquetaPeriodo(): string {
    const desde = this.resumen?.desde ?? this.desde;
    const hasta = this.resumen?.hasta ?? this.hasta;
    if (this.periodo === 'hoy') return 'Entró hoy';
    if (this.periodo === 'rango' || this.resumen?.recortado) {
      return desde === hasta ? `Entró el ${this.fechaCorta(desde)}` : `Entró del ${this.fechaCorta(desde)} al ${this.fechaCorta(hasta)}`;
    }
    return `Entró en los últimos ${this.periodo} días`;
  }

  get recortado(): boolean {
    return !!(this.resumen?.recortado || this.lista?.recortado);
  }

  get corteHistorico(): string | null {
    const f = this.resumen?.historicasAntesDe;
    return f ? this.fechaCorta(f) : null;
  }

  /** Período que de verdad se está mostrando, en dd/mm/aaaa. */
  get periodoMostrado(): string {
    const desde = this.lista?.desde ?? this.resumen?.desde ?? this.desde;
    const hasta = this.lista?.hasta ?? this.resumen?.hasta ?? this.hasta;
    return `del ${this.fechaCorta(desde)} al ${this.fechaCorta(hasta)}`;
  }

  clave(f: FilaEntrada): string {
    return `${f.origen}:${f.id}`;
  }

  trackItem(_i: number, item: ItemLista): string {
    return item.tipo === 'dia' ? `d:${item.etiqueta}` : item.clave;
  }

  iniciales(nombre: string | null | undefined): string {
    const partes = String(nombre ?? '').trim().split(/\s+/).filter(Boolean);
    return partes.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
  }

  formatoMonto(monto: number | null | undefined, moneda: MonedaConciliacion = this.moneda): string {
    const n = Number(monto) || 0;
    if (moneda === 'CLP') return `$${Math.round(n).toLocaleString('es-CL')}`;
    const texto = n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return moneda === 'VES' ? `Bs ${texto}` : `${texto} USDT`;
  }

  hora(iso: string): string {
    return new Intl.DateTimeFormat('es-CL', { timeZone: ZONA, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
  }

  horaActualizado(): string {
    return this.actualizado ? this.hora(this.actualizado.toISOString()) : '';
  }

  // ─── Utilidades ─────────────────────────────────────────────────────────────

  private agruparPorDia(filas: FilaEntrada[]): ItemLista[] {
    const items: ItemLista[] = [];
    let dia = '';
    const hoy = this.hoy();
    for (const fila of filas) {
      const d = this.diaMesa(fila.fecha);
      if (d !== dia) {
        dia = d;
        items.push({ tipo: 'dia', etiqueta: this.etiquetaDia(d, hoy) });
      }
      items.push({ tipo: 'fila', fila, clave: this.clave(fila) });
    }
    return items;
  }

  private etiquetaDia(dia: string, hoy: string): string {
    const [a, m, d] = dia.split('-').map(Number);
    const texto = new Intl.DateTimeFormat('es-CL', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
      new Date(Date.UTC(a, m - 1, d)),
    );
    if (dia === hoy) return `Hoy · ${texto}`;
    if (dia === this.sumarDias(hoy, -1)) return `Ayer · ${texto}`;
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }

  /** `YYYY-MM-DD` de un instante en el reloj de la mesa. */
  private diaMesa(iso: string): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  }

  private hoy(): string {
    return this.diaMesa(new Date().toISOString());
  }

  private sumarDias(fecha: string, dias: number): string {
    const [a, m, d] = fecha.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
  }

  private fechaValida(valor: unknown): string | null {
    const s = String(valor ?? '');
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  }

  private fechaCorta(fecha: string): string {
    const [a, m, d] = String(fecha).split('-');
    return d && m && a ? `${d}/${m}/${a}` : '';
  }

  private mensajeDeError(e: any): string {
    // El ErrorInterceptor del panel relanza algunos errores como texto.
    if (typeof e === 'string' && e.trim()) return e;
    if (e instanceof Error && e.message) return e.message;
    if (e?.status === 401 || e?.status === 403) return 'Tu sesión no tiene permiso de operador.';
    if (e?.status === 429) return 'Hay muchas consultas a la vez. Intenta de nuevo en un minuto.';
    const msg = e?.error?.message;
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg)) return msg.join(' · ');
    return 'No se pudo cargar la información. Intenta de nuevo.';
  }
}
